import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Autocomplete, Icon, IconButton, SxProps, TextField, Theme, Tooltip } from "@mui/material";
import DialogContext from 'components/confirm-dialog-context';
import useCatchApiError from 'hooks/useCatchApiError';
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import { BEEPSOUND } from 'pages/registra-venda/registra-venda';
import { CupomTrocaSkuDTO } from 'pages/registra-venda/registra-venda.model';
import RegistraVendaService from 'pages/registra-venda/registra-venda.service';
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useAppSelector } from 'redux/hooks';

import { theme } from 'theme';
import useCustomLocation from 'utils/useCustomLocation';
import { includesStringInOrder } from 'utils/util';

export type ModoBuscaSku = 'CODIGO_BARRAS' | 'DESCRICAO' | 'CUPOM_TROCA';

type Props<T> = {
    onSelect: (item: T | null, isAdding: boolean) => void;
    disabled?: boolean;

    porCodigoSx?: SxProps<Theme> | undefined;
    porProdutoSx?: SxProps<Theme> | undefined;
    porCupomSx?: SxProps<Theme> | undefined;

    customOptions?: T[];
    getOptionLabel: (item: T) => string;
    getKey?: (item: T) => string;

    permiteCupomTroca?: boolean;
    onSelectCupomTroca?: (cupom: CupomTrocaSkuDTO, isAdding: boolean) => void;
};

export type SkuInputComponent = {
    isAdding: boolean;
    focus: () => void;
};

type LocalState<T> = {
    isAdding: boolean;
    modoBusca: ModoBuscaSku;
    skusFiltro: string;
    skusOptions: T[];
};

const modosDisponiveis = (permiteCupomTroca: boolean): ModoBuscaSku[] =>
    permiteCupomTroca ? ['CODIGO_BARRAS', 'DESCRICAO', 'CUPOM_TROCA'] : ['CODIGO_BARRAS', 'DESCRICAO'];

const SkuInputInner = <T extends object>(props: Props<T>, ref: React.Ref<SkuInputComponent>) => {

    // refs
    const codigoBarrasRef = useRef<HTMLInputElement>(null);
    const skuRef = useRef<HTMLInputElement>(null);
    const cupomTrocaRef = useRef<HTMLInputElement>(null);
    const skusRef = useRef<boolean>(false);
    const modoBuscaRef = useRef<ModoBuscaSku>('CODIGO_BARRAS');
    const permiteCupomTrocaRef = useRef(false);
    permiteCupomTrocaRef.current = props.permiteCupomTroca ?? false;
    const onSelectCupomTrocaRef = useRef(props.onSelectCupomTroca);
    onSelectCupomTrocaRef.current = props.onSelectCupomTroca;

    const initialState: LocalState<T> = {
        isAdding: true,
        modoBusca: 'CODIGO_BARRAS',
        skusFiltro: '',
        skusOptions: [],
    };

    const isRequesMode = !props.customOptions;

    // hooks
    const [localState, setLocalState] = useState(initialState);
    const service = RegistraVendaService();
    const configuracaoBalcao = useAppSelector(e => e.configuracoes.configuracaoPedidoBalcao);
    const tabelaPrecoConsignado = useAppSelector(e => e.consignado.tabelaPreco);
    const estabelecimentoSelecionado = useAppSelector(e => e.estabelecimentoSelecionado);
    const produtosTroca = useAppSelector(s => s.state.pedido.itensTroca);
    const { throwErrorMessage } = useCatchApiError();
    const dialog = useContext(DialogContext);
    const { isTelaRegistraConsignado, isTelaDevolucaoConsignado } = useCustomLocation();
    const { addError } = useSnackbarCustom();

    useImperativeHandle(ref, () => ({
        isAdding: localState.isAdding,
        focus,
    }));

    useEffect(() => {
        if (props.permiteCupomTroca || modoBuscaRef.current !== 'CUPOM_TROCA') return;
        modoBuscaRef.current = 'CODIGO_BARRAS';
        setLocalState(prev => ({ ...prev, modoBusca: 'CODIGO_BARRAS' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.permiteCupomTroca]);

    const getTabelaDePreco = () => {
        if (isTelaRegistraConsignado() || isTelaDevolucaoConsignado()) {
            return tabelaPrecoConsignado;
        }
        return configuracaoBalcao.tabelaPreco;
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        getSkusAc(localState.skusFiltro, getTabelaDePreco()?.uuid ?? null);
        //eslint-disable-next-line
    }, [getTabelaDePreco(), estabelecimentoSelecionado]);

    useEffect(() => {
        setLocalState(prev => ({ ...prev, skusOptions: props.customOptions ?? [] }));
    }, [props.customOptions]);

    let timeout = useRef<number | NodeJS.Timeout>(0);

    useEffect(() => {
        if (skusRef.current || !localState.skusFiltro.length) {
            skusRef.current = false;
            clearTimeout(timeout.current);
            timeout.current = setTimeout(() => {
                getSkusAc(localState.skusFiltro, getTabelaDePreco()?.uuid ?? null);
            }, 500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skusRef.current, localState.skusFiltro, getTabelaDePreco()]);

    const handleKeyDown = (e: KeyboardEvent) => {
        const conditionToToggle = document.activeElement?.id.indexOf('acSku') !== -1;
        switch (e.key) {
            case "F4":
                e.preventDefault();
                alternarModoBusca(conditionToToggle);
                break;
            case "+":
            case "=":
                if (e.altKey) {
                    setLocalState(prevState => ({ ...prevState, isAdding: true }));
                }
                break;
            case "-":
            case "_":
                if (e.altKey) {
                    setLocalState(prevState => ({ ...prevState, isAdding: false }));
                }
                break;
        }
    };

    const onInputChangeSkuAc = useCallback((value: string) => {
        setLocalState(prevState => ({ ...prevState, skusFiltro: value }));
        //eslint-disable-next-line
    }, []);

    const getSkus = useCallback(async (toSearch: string, tabelaPrecoUUID: string | null) => {
        try {
            const { data } = await service.getSkusAc(toSearch, tabelaPrecoUUID);
            setLocalState(prevState => ({ ...prevState, skusOptions: data as T[] }));
        } catch (err) {
            throwErrorMessage(err ?? []);
        }
        //eslint-disable-next-line
    }, [estabelecimentoSelecionado]);

    const getSkusAc = useCallback((toSearch: string = '', tabelaPrecoUUID: string | null) => {
        if (!isRequesMode) return;
        getSkus(toSearch, tabelaPrecoUUID);
        //eslint-disable-next-line
    }, [estabelecimentoSelecionado]);

    const skusOptions = () => {
        const skus = localState.skusOptions;
        if (!skus) return [];

        const filtro = localState.skusFiltro;
        if (!filtro) { return skus; }

        const toReturn = skus.filter(e => {
            let skuFormat = props.getOptionLabel(e);
            return includesStringInOrder(skuFormat, filtro);
        });

        if (!toReturn.length && filtro) {
            skusRef.current = true;
            return skus;
        }

        return toReturn;
    };

    // onBeepSku é memoizado sem as props nas deps: sem esta ref, o beep dispara o onSelect
    // capturado num render antigo (ex.: sem o desconto fixo recém-autorizado).
    const onSelectRef = useRef(props.onSelect);
    onSelectRef.current = props.onSelect;

    const onBeepSku = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const inputValue = codigoBarrasRef.current?.value;
        if (e.key === 'Enter' && inputValue) {
            service.getProdutoByCodigo(inputValue, getTabelaDePreco()?.uuid ?? null)
                .then(({ data }) => {
                    let found: T | null = data as T;
                    if (!!props.getKey) {
                        found = localState.skusOptions.find(s => props.getKey?.(s) === data.uuid) ?? null;
                    }
                    onSelectRef.current(found, localState.isAdding);
                }).catch(error => {
                    BEEPSOUND.play();
                    dialog.confirm({
                        error: true,
                        title: 'Atenção',
                        message: `CÓDIGO DE BARRAS ${inputValue} NÃO ENCONTRADO NA TABELA DE PREÇO ${getTabelaDePreco()?.descritivo ?? ""}`,
                        onConfirm: () => { }
                    });
                });
            codigoBarrasRef.current.value = '';
            focus();
        }
        //eslint-disable-next-line
    }, [getTabelaDePreco(), localState.skusOptions, localState.isAdding]);

    const onBlurSkus = useCallback(() => {
        if (!localState.skusFiltro) {
            getSkusAc("", getTabelaDePreco()?.uuid ?? null);
        }
        //eslint-disable-next-line
    }, [localState.skusFiltro, getTabelaDePreco()]);

    const toggleAdd = useCallback(() => {
        setLocalState(prevState => ({ ...prevState, isAdding: !prevState.isAdding }));
        focus();
        //eslint-disable-next-line
    }, []);

    const alternarModoBusca = useCallback((deveAlternar: boolean) => {
        if (deveAlternar) {
            const modos = modosDisponiveis(permiteCupomTrocaRef.current);
            const proximoIndice = (modos.indexOf(modoBuscaRef.current) + 1) % modos.length;
            modoBuscaRef.current = modos[proximoIndice];
            setLocalState(prev => ({ ...prev, modoBusca: modoBuscaRef.current }));
        };
        focus();
        //eslint-disable-next-line
    }, []);

    const onBeepCupomTroca = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const codigo = cupomTrocaRef.current?.value?.trim();
        if (e.key === 'Enter' && codigo) {
            service.getSkuPorCupomTroca(codigo)
                .then(({ data }) => {
                    const jaLido = produtosTroca.some(item => item.cupomTroca?.codigo === data.codigo);

                    if (!localState.isAdding) {
                        if (!jaLido) {
                            BEEPSOUND.play();
                            return addError({
                                id: 'erro-cupom-troca-nao-encontrado',
                                message: `Cupom de troca ${data.codigo} não foi lido nesta venda`,
                                closeable: true,
                                preventDuplicate: true,
                            });
                        }
                        return onSelectCupomTrocaRef.current?.(data, localState.isAdding);
                    }

                    if (jaLido) {
                        BEEPSOUND.play();
                        return addError({
                            id: 'erro-cupom-troca-duplicado',
                            message: `Cupom de troca ${data.codigo} já foi lido nesta venda`,
                            closeable: true,
                            preventDuplicate: true,
                        });
                    }
                    onSelectCupomTrocaRef.current?.(data, localState.isAdding);
                }).catch(() => {
                    BEEPSOUND.play();
                    dialog.confirm({
                        error: true,
                        title: 'Atenção',
                        message: `CUPOM DE TROCA ${codigo} NÃO ENCONTRADO`,
                        onConfirm: () => { }
                    });
                }).finally(() => {
                    if (cupomTrocaRef.current) cupomTrocaRef.current.value = '';
                    focus();
                });
        }
        //eslint-disable-next-line
    }, [service, localState.isAdding, produtosTroca]);

    const focus = useCallback(() => {
        setTimeout(() => {
            if (modoBuscaRef.current === 'DESCRICAO') {
                skuRef.current?.focus();
            } else if (modoBuscaRef.current === 'CUPOM_TROCA') {
                cupomTrocaRef.current?.focus();
            } else {
                codigoBarrasRef.current?.focus();
            }
        },);
    }, []);

    return <>
        {localState.modoBusca === 'CODIGO_BARRAS' &&
            <TextField
                disabled={props.disabled}
                fullWidth={true}
                placeholder={getTabelaDePreco()?.descritivo ?? ""}
                variant="standard"
                label='Código de barras | Código do sku'
                InputLabelProps={{
                    sx: {
                        marginLeft: '5px'
                    }
                }}
                id="acSku-input"
                sx={{
                    borderRadius: '5px',
                    maxHeight: "70px",
                    ...props.porCodigoSx
                }}
                InputProps={{
                    startAdornment: (
                        <>
                            <Tooltip
                                title={`${!localState.isAdding ? "Adicionar item ( Alt + '+' )" : "Remover item ( Alt + '-' )"}`}>
                                <IconButton
                                    disabled={props.disabled}
                                    onClick={toggleAdd}
                                    sx={{ height: 0, padding: 0, paddingLeft: '5px', paddingRight: '5px' }}
                                >
                                    <Icon className={`${localState.isAdding ? "fa-solid fa-plus" : "fa-solid fa-minus"}`}
                                        sx={{
                                            color: props.disabled ? theme.palette.text.disabled : theme.palette.primary.main,
                                            paddingBottom: '5px'
                                        }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    ),
                    endAdornment: (
                        <React.Fragment>
                            <h4 className="h4-formatado">(F4)</h4>
                            <IconButton
                                disabled={props.disabled}
                                onClick={() => {
                                    alternarModoBusca(true);
                                }}
                                sx={{ height: 0, padding: '0 5px' }}
                            >
                                <Icon className="fa-solid fa-barcode" />
                            </IconButton>
                        </React.Fragment>
                    )
                }}
                inputRef={codigoBarrasRef}
                onKeyUp={onBeepSku}
            />}

        {localState.modoBusca === 'CUPOM_TROCA' &&
            <TextField
                disabled={props.disabled}
                fullWidth={true}
                placeholder={getTabelaDePreco()?.descritivo ?? ""}
                variant="standard"
                label='Cupom de troca'
                InputLabelProps={{
                    sx: {
                        marginLeft: '5px'
                    }
                }}
                id="acSkuCupomTroca-input"
                sx={{
                    borderRadius: '5px',
                    maxHeight: "70px",
                    ...props.porCupomSx
                }}
                InputProps={{
                    startAdornment: (
                        <>
                            <Tooltip
                                title={`${!localState.isAdding ? "Adicionar item ( Alt + '+' )" : "Remover item ( Alt + '-' )"}`}>
                                <IconButton
                                    disabled={props.disabled}
                                    onClick={toggleAdd}
                                    sx={{ height: 0, padding: 0, paddingLeft: '5px', paddingRight: '5px' }}
                                >
                                    <Icon className={`${localState.isAdding ? "fa-solid fa-plus" : "fa-solid fa-minus"}`}
                                        sx={{
                                            color: props.disabled ? theme.palette.text.disabled : theme.palette.primary.main,
                                            paddingBottom: '5px'
                                        }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    ),
                    endAdornment: (
                        <React.Fragment>
                            <h4 className="h4-formatado">(F4)</h4>
                            <IconButton
                                disabled={props.disabled}
                                onClick={() => {
                                    alternarModoBusca(true);
                                }}
                                sx={{ height: 0, padding: '0 5px' }}
                            >
                                <Icon className="fa-solid fa-receipt" />
                            </IconButton>
                        </React.Fragment>
                    )
                }}
                inputRef={cupomTrocaRef}
                onKeyUp={onBeepCupomTroca}
            />}

        {localState.modoBusca === 'DESCRICAO' &&
            <Autocomplete
                disablePortal
                disabled={props.disabled}
                onBlur={onBlurSkus}
                id="acSkuProcurandoProduto-input"
                options={skusOptions()}
                getOptionLabel={props.getOptionLabel}
                onInputChange={(_, value) => onInputChangeSkuAc(value)}
                onChange={(_, value) => props.onSelect(value, localState.isAdding)}
                value={null}
                blurOnSelect={true}
                filterOptions={(x) => x}
                noOptionsText={'Nenhum resultado encontrado'}
                renderInput={
                    (params) => <TextField {...params}
                        sx={{ ...props.porProdutoSx }}
                        label="Nome tamanho cor do sku"
                        variant="standard"
                        placeholder={getTabelaDePreco()?.descritivo ?? "NOME DA TABELA DE PREÇO PADRÃO"}
                        inputRef={skuRef}
                        InputLabelProps={{
                            sx: {
                                marginLeft: '5px'
                            }
                        }}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <Tooltip title={`${!localState.isAdding ? "Adicionar item ( Alt + '+' )" : "Remover item ( Alt + '-' )"}`}>
                                        <IconButton
                                            disabled={props.disabled}
                                            onClick={toggleAdd}
                                            sx={{ height: 0, padding: 0, paddingLeft: '5px', paddingRight: '5px' }}
                                        >
                                            <Icon className={`${localState.isAdding ? "fa-solid fa-plus" : "fa-solid fa-minus"}`}
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    paddingBottom: '5px'
                                                }} />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ),
                            endAdornment: (
                                <React.Fragment>
                                    <h4 className="h4-formatado">(F4)</h4>
                                    <IconButton
                                        disabled={props.disabled}
                                        onClick={() => {
                                            alternarModoBusca(true);
                                        }}
                                        sx={{ height: 0, padding: 0, paddingLeft: '5px' }}
                                    >
                                        <LocalOfferIcon />
                                    </IconButton>
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                    />}
            />}
    </>;
};

const SkuInput = forwardRef(SkuInputInner) as <T>(
    props: Props<T> & { ref?: React.Ref<SkuInputComponent>; }
) => ReturnType<typeof SkuInputInner>;


export default SkuInput;
